import { Lightning, Snowflake, Desktop, Wrench } from "@phosphor-icons/react";

/**
 * Category picker — the "corps de métier" of the intervention.
 *
 * Distinct from `InterventionTypeToggle` (intervention vs dépannage): category
 * is WHAT the tech will do, type is HOW it's scheduled (planned vs urgent).
 * Icon + colour per category makes the agenda pill instantly readable.
 *
 * Colours are the source of truth — reused by `CategorieBadge` and by any
 * agenda view that renders an intervention pill.
 */
export const CATEGORIES = {
  electricite:   { label: "Électricité",   color: "#F59E0B", icon: Lightning },
  climatisation: { label: "Climatisation", color: "#06B6D4", icon: Snowflake },
  informatique:  { label: "Informatique",  color: "#8B5CF6", icon: Desktop },
  divers:        { label: "Divers",        color: "#94A3B8", icon: Wrench },
};

const ORDER = ["electricite", "climatisation", "informatique", "divers"];

export default function InterventionCategoryPicker({ value, onChange, testid = "iv-categorie" }) {
  const current = value || "electricite";
  return (
    <div className="grid grid-cols-4 gap-1.5" data-testid={testid}>
      {ORDER.map((k) => {
        const c = CATEGORIES[k];
        const on = current === k;
        const Icon = c.icon;
        return (
          <button
            key={k}
            type="button"
            onClick={() => onChange(k)}
            className={`flex flex-col items-center justify-center gap-0.5 rounded-xl border py-2 transition-all ${
              on
                ? "text-white shadow-[0_0_18px_rgba(0,0,0,0.4)]"
                : "bg-white/5 border-white/10 text-white/55 hover:border-white/25"
            }`}
            style={on ? { background: c.color + "22", borderColor: c.color } : {}}
            data-testid={`${testid}-${k}`}
            title={c.label}
          >
            <Icon
              size={16}
              weight={on ? "fill" : "duotone"}
              style={{ color: on ? c.color : undefined }}
            />
            <span className="text-[10px] font-semibold leading-tight">{c.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Small colour-coded pill for the trade category. Renders nothing when the
 * intervention has no category (legacy data) so it degrades gracefully.
 */
export function CategorieBadge({ value, size = "sm", showLabel = true, className = "" }) {
  const c = value && CATEGORIES[value];
  if (!c) return null;
  const px = size === "xs"
    ? "px-1 py-[1px] text-[8px]"
    : size === "md"
      ? "px-2 py-0.5 text-[11px]"
      : "px-1.5 py-[1px] text-[9px]";
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full font-bold shrink-0 ${px} ${className}`}
      style={{ background: c.color + "22", color: c.color }}
      data-testid={`cat-badge-${value}`}
      title={c.label}
    >
      <span aria-hidden>●</span>
      {showLabel && <span>{c.label}</span>}
    </span>
  );
}
